import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { handleError, useErrorHandler } from './errorHandler';

// 统一的异步操作类型定义
interface AsyncOperationContext {
  context: string;
  operationName?: string;
}

interface AsyncOperationResult<T = any> {
  success: boolean;
  data?: T;
  error?: Error;
  context: string;
}

// 通用异步操作封装
export const useAsyncOperations = () => {
  const { handleError } = useErrorHandler();
  const queryClient = useQueryClient();
  
  // 统一的安全操作封装
  const safeAsyncOperation = async <T>(
    operation: () => Promise<T>,
    context: string
  ): Promise<AsyncOperationResult<T>> => {
    try {
      const data = await operation();
      return {
        success: true,
        data,
        context
      };
    } catch (error) {
      handleError(error, { context });
      return {
        success: false,
        error: error as Error,
        context
      };
    }
  };
  
  // 乐观更新封装
  const optimisticUpdate = <T>(
    updateFn: () => void,
    asyncOperation: () => Promise<T>,
    rollbackFn?: () => void,
    context: string = 'optimistic-update'
  ): Promise<AsyncOperationResult<T>> => {
    // 先执行乐观更新
    updateFn();
    
    return safeAsyncOperation(async () => {
      try {
        return await asyncOperation();
      } catch (error) {
        // 如果异步操作失败，回滚乐观更新
        if (rollbackFn) {
          rollbackFn();
        }
        throw error;
      }
    }, context);
  };
  
  // 批量操作封装
  const batchOperation = async <T>(
    operations: Array<() => Promise<T>>,
    context: string = 'batch-operation'
  ): Promise<AsyncOperationResult<T[]>> => {
    return safeAsyncOperation(async () => {
      const results = await Promise.allSettled(operations.map(op => op()));
      
      const successfulResults: T[] = [];
      const errors: Error[] = [];
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulResults.push(result.value);
        } else {
          errors.push(result.reason as Error);
          handleError(result.reason, { context: `${context}-operation-${index}` });
        }
      });
      
      if (errors.length > 0) {
        throw new Error(`批量操作失败: ${errors.length}个操作失败`);
      }
      
      return successfulResults;
    }, context);
  };
  
  return {
    safeAsyncOperation,
    optimisticUpdate,
    batchOperation,
    queryClient
  };
};

// 向后兼容的section专用版本
export const useSectionAsyncOperations = () => {
  const { safeAsyncOperation, queryClient } = useAsyncOperations();
  
  return {
    safeAsyncOperation,
    queryClient
  };
};